import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { I18nService } from 'i18n';
import { Subscription } from 'rxjs';
import { Logger } from '../services/logger.service';

/**
 * Locale-aware number formatting pipe that reacts to language changes.
 *
 * Usage: {{ value | localeNumber:'1.2-2' }}
 *
 * The pipe automatically updates when the application language changes,
 * formatting numbers according to the active locale conventions.
 *
 * Examples:
 * - English (en): 1,234.56
 * - German (de): 1.234,56
 */
@Pipe({
  name: 'localeNumber',
  standalone: true,
  pure: false // Required to react to language changes
})
export class LocaleNumberPipe implements PipeTransform, OnDestroy {
  protected logger = new Logger('LocaleNumberPipe');
  private decimalPipe?: DecimalPipe;
  private currentLocale?: string;
  private langSubscription?: Subscription;
  private cachedValue: string | null = null;
  private lastValue?: number;
  private lastDigitsInfo?: string;

  constructor(private i18nService: I18nService) {
    // Subscribe to language changes
    this.langSubscription = this.i18nService.currentLanguage$.subscribe(lang => {
      this.updateLocale(lang);
      // Clear cache when locale changes
      this.cachedValue = null;
      this.lastValue = undefined;
    });

    // Initialize with current language
    this.updateLocale(this.i18nService.getLanguage());
  }

  ngOnDestroy(): void {
    this.langSubscription?.unsubscribe();
  }

  private updateLocale(lang: string): void {
    // Map language codes to Angular locale codes
    const localeMap: Record<string, string> = {
      'en': 'en-US',
      'de': 'de-DE',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'pt': 'pt-PT',
      'it': 'it-IT'
    };

    const langCode = lang.split('-')[0];
    this.currentLocale = localeMap[langCode] || 'en-US';
    this.decimalPipe = new DecimalPipe(this.currentLocale);
  }

  transform(value: number | string | null | undefined, digitsInfo?: string): string | null {
    if (value == null || value === '') {
      return null;
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return null;
    }

    // Use cache if value and digitsInfo haven't changed
    if (this.cachedValue !== null &&
        this.lastValue === numValue &&
        this.lastDigitsInfo === digitsInfo) {
      return this.cachedValue;
    }

    this.lastValue = numValue;
    this.lastDigitsInfo = digitsInfo;

    try {
      const result = this.decimalPipe?.transform(numValue, digitsInfo);
      this.cachedValue = result !== undefined ? result : null;
      return this.cachedValue;
    } catch (error) {
      this.logger.error('LocaleNumberPipe error:', error);
      return String(numValue);
    }
  }
}
